import { useState, useEffect, useCallback } from 'react';
import { AdminLayout } from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Tooltip as ShadTooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import {
  TrendingUp, TrendingDown, ShoppingBag, Euro, Users, Store, BarChart3,
  ArrowUpRight, ArrowDownRight, Clock, Target, Download, HelpCircle,
  Heart, AlertTriangle, Shield, Repeat, Ban, Star, CalendarDays
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area, PieChart as RechartsPieChart, Pie, Cell, LineChart, Line,
  ReferenceLine, Legend
} from 'recharts';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

const periodLabels = { '7d': '7 jours', '14d': '14 jours', '30d': '30 jours', '90d': '90 jours', '365d': '1 an' };

/* ─── KPI Card ─── */
const KPICard = ({ title, value, suffix = '', growth, icon: Icon, color, subline, tooltip, children }) => {
  const pos = growth >= 0;
  const bg = { emerald: 'bg-emerald-50 text-emerald-600', blue: 'bg-blue-50 text-blue-600', purple: 'bg-purple-50 text-purple-600', amber: 'bg-amber-50 text-amber-600', rose: 'bg-rose-50 text-rose-600', cyan: 'bg-cyan-50 text-cyan-600', slate: 'bg-slate-100 text-slate-600' }[color] || 'bg-gray-50 text-gray-600';

  return (
    <Card data-testid={`kpi-${title.toLowerCase().replace(/[^a-z]/g, '-')}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-1">
          <div className="flex items-center gap-1.5">
            <p className="text-sm font-medium text-gray-500">{title}</p>
            {tooltip && (
              <TooltipProvider delayDuration={200}>
                <ShadTooltip>
                  <TooltipTrigger asChild><HelpCircle className="w-3.5 h-3.5 text-gray-300 cursor-help" /></TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs text-xs">{tooltip}</TooltipContent>
                </ShadTooltip>
              </TooltipProvider>
            )}
          </div>
          <div className={`w-9 h-9 rounded-lg ${bg} flex items-center justify-center`}>
            <Icon className="w-4.5 h-4.5" />
          </div>
        </div>
        <p className="text-2xl font-bold text-gray-900">
          {typeof value === 'number' ? value.toLocaleString('fr-FR', { maximumFractionDigits: 2 }) : value}
          {suffix && <span className="text-base ml-0.5 font-semibold text-gray-500">{suffix}</span>}
        </p>
        {growth !== undefined && (
          <div className={`flex items-center gap-1 text-xs mt-1 ${pos ? 'text-emerald-600' : 'text-red-500'}`}>
            {pos ? <ArrowUpRight className="w-3.5 h-3.5" /> : <ArrowDownRight className="w-3.5 h-3.5" />}
            <span className="font-semibold">{Math.abs(growth)}%</span>
            <span className="text-gray-400">vs période préc.</span>
          </div>
        )}
        {subline && <p className="text-xs text-gray-400 mt-1.5">{subline}</p>}
        {children}
      </CardContent>
    </Card>
  );
};

/* ─── Health Gauge ─── */
const HealthGauge = ({ score, label, details }) => {
  const color = score >= 80 ? '#10b981' : score >= 50 ? '#f59e0b' : '#ef4444';
  return (
    <Card data-testid="kpi-health">
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-1.5">
            <p className="text-sm font-medium text-gray-500">Sante globale</p>
            <TooltipProvider delayDuration={200}>
              <ShadTooltip>
                <TooltipTrigger asChild><HelpCircle className="w-3.5 h-3.5 text-gray-300 cursor-help" /></TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs text-xs">
                  Score base sur : completion ({details?.completion}/25), annulations ({details?.cancellation}/25), recurrence ({details?.recurring}/25), operations ({details?.operations}/25)
                </TooltipContent>
              </ShadTooltip>
            </TooltipProvider>
          </div>
          <Shield className="w-5 h-5" style={{ color }} />
        </div>
        <div className="flex items-end gap-3">
          <span className="text-3xl font-bold" style={{ color }}>{score}</span>
          <span className="text-sm text-gray-400 mb-1">/ 100</span>
        </div>
        <Badge className="mt-1" style={{ backgroundColor: `${color}20`, color, border: 'none' }}>{label}</Badge>
      </CardContent>
    </Card>
  );
};

export default function AdminAnalytics() {
  useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30d');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [showCustom, setShowCustom] = useState(false);

  const fetch_ = useCallback(async () => {
    try {
      setLoading(true);
      let url = `${API}/admin/analytics/advanced?period=${period}`;
      if (period === 'custom' && customStart && customEnd) {
        url = `${API}/admin/analytics/advanced?start_date=${customStart}&end_date=${customEnd}`;
      }
      const r = await axios.get(url);
      setData(r.data);
    } catch {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [period, customStart, customEnd]);

  useEffect(() => { fetch_(); }, [fetch_]);

  const exportCSV = () => {
    if (!data) return;
    const rows = [['Date', 'Commandes', 'Terminees', 'Annulees', 'GMV', 'Revenue'], ...data.chart_data.map(d => [d.date, d.total, d.completed, d.cancelled, d.gmv, d.revenue])];
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `lemo_analytics_${period}.csv`;
    a.click();
    toast.success('Export telecharge');
  };

  if (loading || !data) {
    return (
      <AdminLayout title="Analytics">
        <div className="space-y-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">{[...Array(7)].map((_, i) => <div key={i} className="h-28 bg-gray-100 rounded-2xl animate-pulse" />)}</div>
          <div className="grid lg:grid-cols-2 gap-4">{[...Array(4)].map((_, i) => <div key={i} className="h-72 bg-gray-100 rounded-2xl animate-pulse" />)}</div>
        </div>
      </AdminLayout>
    );
  }

  const { summary: s, clients: c, restaurants: r, health: h, chart_data, avg_daily_orders, top_restaurants, concentration, retention, cancellations, order_by_status, hourly_distribution } = data;

  const chartFmt = (chart_data || []).map(d => ({
    ...d,
    label: d.date ? new Date(d.date + 'T00:00:00').toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }) : ''
  }));

  const statusLabels = { 'Reçue': 'Recues', 'Planifiée': 'Planifiees', 'En préparation': 'En preparation', 'Prête': 'Pretes', 'Terminée': 'Terminees', 'Annulée': 'Annulees', 'pending_payment': 'Paiement en cours' };
  const statusData = Object.entries(order_by_status || {}).filter(([k]) => k !== 'Panier').map(([k, v]) => ({ name: statusLabels[k] || k, value: v }));

  return (
    <AdminLayout title="Analytics">
      <div className="space-y-6">
        {/* ─── Header ─── */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div>
            <p className="text-sm text-gray-500">Periode : {data.period?.start_date} &rarr; {data.period?.end_date}</p>
          </div>
          <div className="flex gap-2 flex-wrap items-center">
            {Object.entries(periodLabels).map(([k, v]) => (
              <Button key={k} variant={period === k ? 'default' : 'outline'} size="sm" onClick={() => { setPeriod(k); setShowCustom(false); }} className={period === k ? 'bg-emerald-500 hover:bg-emerald-600' : ''} data-testid={`period-${k}`}>{v}</Button>
            ))}
            <Button variant={showCustom ? 'default' : 'outline'} size="sm" onClick={() => setShowCustom(!showCustom)} className={showCustom ? 'bg-emerald-500 hover:bg-emerald-600' : ''} data-testid="period-custom">
              <CalendarDays className="w-4 h-4 mr-1" />Personnalise
            </Button>
            <Button variant="outline" size="sm" onClick={exportCSV} data-testid="export-btn"><Download className="w-4 h-4 mr-1.5" />Export</Button>
          </div>
        </div>
        {showCustom && (
          <div className="flex flex-wrap gap-3 items-end p-4 bg-gray-50 rounded-xl border border-gray-200">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Date debut</label>
              <Input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)} className="w-40" data-testid="custom-start" />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Date fin</label>
              <Input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)} className="w-40" data-testid="custom-end" />
            </div>
            <Button size="sm" onClick={() => { if (customStart && customEnd) setPeriod('custom'); else toast.error('Selectionnez les deux dates'); }} className="bg-emerald-500 hover:bg-emerald-600" data-testid="apply-custom">Appliquer</Button>
          </div>
        )}

        {/* ─── Row 1 : 4 KPIs ─── */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KPICard title="GMV (Volume Brut)" value={s.gmv_net} suffix="€" growth={s.gmv_growth} icon={Euro} color="emerald" subline={`GMV net apres annulations`} tooltip="GMV = somme du montant brut de toutes les commandes, avant commission. GMV net = GMV - commandes annulees/remboursees." />
          <KPICard title="Revenus plateforme" value={s.revenue} suffix="€" growth={s.revenue_growth} icon={TrendingUp} color="blue" subline={`Take rate effectif : ${s.take_rate}%`} tooltip={`Revenus = commissions encaissees apres annulations. Take rate = Revenus / GMV net. Commission nominale : ${s.commission_rate}%.`} />
          <KPICard title="Commandes" value={s.orders} growth={s.orders_growth} icon={ShoppingBag} color="purple" subline={`Completion : ${s.completion_rate}%`} tooltip="Nombre total de commandes sur la periode. Taux de completion = commandes terminees / commandes totales." />
          <KPICard title="Panier moyen (AOV)" value={s.aov} suffix="€" growth={s.aov_growth} icon={Target} color="amber" subline={`${s.orders_above_20}% des commandes > 20 €`} tooltip="AOV = GMV net / nombre de commandes actives." />
        </div>

        {/* ─── Row 2 : 3 KPIs ─── */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <KPICard title="Clients" value={c.new} icon={Users} color="blue" growth={c.new_growth} subline={`Recurrents : ${c.recurring_rate}%`} tooltip="Nouveaux clients = inscrits dans la periode. Recurrents = clients ayant deja commande avant la periode.">
            <Badge variant="outline" className="mt-1.5 text-xs border-blue-200 bg-blue-50 text-blue-600">Repeat 90j : {c.repeat_rate_90d}%</Badge>
          </KPICard>

          <KPICard title="Commercants" value={r.active} icon={Store} color="emerald" subline={`${r.at_risk?.length || 0} a risque (14j sans commande)`} tooltip="Commercants actifs = au moins 1 commande dans la periode. A risque = pas de commande depuis 14+ jours.">
            <Badge variant="outline" className="mt-1.5 text-xs border-emerald-200 bg-emerald-50 text-emerald-600">{r.new_activated} nouveau(x) active(s)</Badge>
          </KPICard>

          <HealthGauge score={h.score} label={h.label} details={h.details} />
        </div>

        {/* ─── Charts Row 1 ─── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* GMV & Revenue Evolution */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base"><BarChart3 className="w-5 h-5 text-emerald-500" />Evolution GMV & Revenus</CardTitle>
            </CardHeader>
            <CardContent>
              {chartFmt.length === 0 ? <p className="text-gray-400 text-center py-12">Aucune donnee</p> : (
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartFmt}>
                      <defs>
                        <linearGradient id="gGmv" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#10b981" stopOpacity={0.2} /><stop offset="95%" stopColor="#10b981" stopOpacity={0} /></linearGradient>
                        <linearGradient id="gRev" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#3b82f6" stopOpacity={0.2} /><stop offset="95%" stopColor="#3b82f6" stopOpacity={0} /></linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} />
                      <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} tickFormatter={v => `${v}€`} />
                      <Tooltip formatter={(v, n) => [`${Number(v).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} €`, n === 'gmv' ? 'GMV' : 'Revenus']} contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb' }} />
                      <Area type="monotone" dataKey="gmv" stroke="#10b981" fill="url(#gGmv)" strokeWidth={2} name="gmv" />
                      <Area type="monotone" dataKey="revenue" stroke="#3b82f6" fill="url(#gRev)" strokeWidth={2} name="revenue" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}
              <div className="flex justify-center gap-6 mt-2 text-sm text-gray-500">
                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />GMV</span>
                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-blue-500" />Revenus</span>
              </div>
            </CardContent>
          </Card>

          {/* Daily Orders Stacked */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base"><ShoppingBag className="w-5 h-5 text-purple-500" />Commandes quotidiennes</CardTitle>
            </CardHeader>
            <CardContent>
              {chartFmt.length === 0 ? <p className="text-gray-400 text-center py-12">Aucune donnee</p> : (
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartFmt}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} />
                      <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} allowDecimals={false} />
                      <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb' }} />
                      <ReferenceLine y={avg_daily_orders} stroke="#94a3b8" strokeDasharray="4 4" label={{ value: `Moy: ${avg_daily_orders}`, position: 'insideTopRight', fontSize: 10, fill: '#94a3b8' }} />
                      <Bar dataKey="completed" stackId="a" fill="#10b981" radius={[0, 0, 0, 0]} name="Terminees" />
                      <Bar dataKey="other" stackId="a" fill="#3b82f6" name="En cours" />
                      <Bar dataKey="cancelled" stackId="a" fill="#ef4444" radius={[3, 3, 0, 0]} name="Annulees" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
              <div className="flex justify-center gap-5 mt-2 text-xs text-gray-500">
                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />Terminees</span>
                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-blue-500" />En cours</span>
                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-red-500" />Annulees</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* ─── Charts Row 2 ─── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Top 10 Restaurants */}
          <Card className="lg:col-span-2">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base"><Store className="w-5 h-5 text-amber-500" />Top 10 Restaurants par GMV</CardTitle>
            </CardHeader>
            <CardContent>
              {(top_restaurants || []).length === 0 ? <p className="text-gray-400 text-center py-8">Aucune donnee</p> : (
                <div className="space-y-2.5">
                  {top_restaurants.map((resto, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors cursor-pointer" data-testid={`top-resto-${i}`}>
                      <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0" style={{ backgroundColor: COLORS[i % COLORS.length] }}>{i + 1}</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-gray-900 text-sm truncate">{resto.name}</p>
                          {resto.badge && <Badge className={`text-[10px] py-0 ${resto.badge === 'En forte croissance' ? 'bg-emerald-50 text-emerald-600 border-emerald-200' : 'bg-red-50 text-red-600 border-red-200'}`}>{resto.badge}</Badge>}
                        </div>
                        <p className="text-xs text-gray-400">{resto.orders} cmd &middot; {resto.share}% du GMV</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="font-semibold text-sm text-gray-900">{resto.gmv.toLocaleString('fr-FR')} €</p>
                        <p className={`text-xs font-medium ${resto.growth >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>{resto.growth >= 0 ? '+' : ''}{resto.growth}%</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* GMV Concentration */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base"><Target className="w-5 h-5 text-rose-500" />Concentration GMV</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4 mt-2">
                {[{ label: 'Top 5', val: concentration.top5, color: '#10b981' }, { label: 'Top 10', val: concentration.top10, color: '#3b82f6' }, { label: 'Long tail', val: concentration.tail, color: '#f59e0b' }].map(item => (
                  <div key={item.label}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-600">{item.label}</span>
                      <span className="font-semibold">{item.val}%</span>
                    </div>
                    <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all duration-700" style={{ width: `${item.val}%`, backgroundColor: item.color }} />
                    </div>
                  </div>
                ))}
                {concentration.alert && (
                  <div className="flex items-center gap-2 p-2.5 bg-amber-50 rounded-lg border border-amber-100 mt-2">
                    <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                    <p className="text-xs text-amber-700">Le Top 5 represente &gt;80% du GMV — forte dependance.</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* ─── Charts Row 3 ─── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Retention */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base"><Repeat className="w-5 h-5 text-blue-500" />Retention & Repeat Purchase (90j)</CardTitle>
            </CardHeader>
            <CardContent>
              {(retention || []).length === 0 ? <p className="text-gray-400 text-center py-8">Pas assez de donnees</p> : (
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={retention}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="orders" tick={{ fontSize: 11 }} tickLine={false} label={{ value: 'Nb commandes', position: 'insideBottom', offset: -2, fontSize: 10, fill: '#9ca3af' }} />
                      <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} label={{ value: 'Clients', angle: -90, position: 'insideLeft', fontSize: 10, fill: '#9ca3af' }} />
                      <Tooltip formatter={v => [v, 'Clients']} contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb' }} />
                      <Bar dataKey="clients" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Cancellations & Disputes */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base"><Ban className="w-5 h-5 text-red-500" />Annulations & litiges</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="text-center p-3 bg-red-50 rounded-xl">
                  <p className="text-xl font-bold text-red-600">{cancellations.count}</p>
                  <p className="text-[10px] text-red-400">Annulees</p>
                </div>
                <div className="text-center p-3 bg-red-50 rounded-xl">
                  <p className="text-xl font-bold text-red-600">{cancellations.rate}%</p>
                  <p className="text-[10px] text-red-400">Taux</p>
                </div>
                <div className="text-center p-3 bg-amber-50 rounded-xl">
                  <p className="text-xl font-bold text-amber-600">{cancellations.dispute_count}</p>
                  <p className="text-[10px] text-amber-400">Litiges</p>
                </div>
              </div>
              {(cancellations.high_cancel_restos || []).length > 0 && (
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-2">Restaurants a taux d'annulation eleve</p>
                  <div className="space-y-1.5">
                    {cancellations.high_cancel_restos.map((r, i) => (
                      <div key={i} className="flex items-center justify-between text-sm p-2 bg-red-50/50 rounded-lg">
                        <span className="text-gray-700 truncate">{r.name}</span>
                        <Badge variant="outline" className="text-red-600 border-red-200 text-xs">{r.rate}% ({r.total} cmd)</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {(cancellations.high_cancel_restos || []).length === 0 && <p className="text-gray-400 text-center text-sm py-4">Aucun restaurant a taux anormal</p>}
            </CardContent>
          </Card>
        </div>

        {/* ─── Charts Row 4 ─── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Hourly Distribution */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base"><Clock className="w-5 h-5 text-cyan-500" />Heures de pointe</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={hourly_distribution || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="hour" tick={{ fontSize: 10 }} tickLine={false} tickFormatter={h => `${h}h`} />
                    <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} allowDecimals={false} />
                    <Tooltip formatter={v => [v, 'Commandes']} labelFormatter={h => `${h}h00 - ${h}h59`} contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb' }} />
                    <Bar dataKey="orders" fill="#06b6d4" radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Order Status Donut */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base"><BarChart3 className="w-5 h-5 text-rose-500" />Repartition des statuts</CardTitle>
            </CardHeader>
            <CardContent>
              {statusData.length === 0 ? <p className="text-gray-400 text-center py-8">Aucune donnee</p> : (
                <div className="flex items-center gap-4">
                  <div className="h-44 w-44 flex-shrink-0">
                    <ResponsiveContainer width="100%" height="100%">
                      <RechartsPieChart>
                        <Pie data={statusData} cx="50%" cy="50%" innerRadius={45} outerRadius={65} paddingAngle={2} dataKey="value">
                          {statusData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                        </Pie>
                        <Tooltip formatter={v => [v, 'Commandes']} />
                      </RechartsPieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="space-y-1.5 flex-1">
                    {statusData.map((s, i) => (
                      <div key={s.name} className="flex items-center gap-2 text-xs">
                        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                        <span className="text-gray-600 truncate">{s.name}</span>
                        <span className="font-medium ml-auto">{s.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
}
